import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { has_access } from "../../../middleware/privileges";
import { GET_WASTE_REPORT, GET_BRANCHES } from "./queries";
import { WasteReport as WasteReportType, WASTE_REASON_LABELS } from "./models";
import InventoryNav from "./inventoryNav";

const btn = {
  txt: lang("Registrar Merma", "Report Waste"),
  icon: "fa fa-plus",
  link: "/inventory/waste/report",
};

const formatCurrency = (val: number) =>
  `$${Number(val).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const WasteReportPage: React.FC = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(
    firstOfMonth.toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));
  const [branchFilter, setBranchFilter] = useState("");

  const [fetchReport, { data, loading }] = useLazyQuery(GET_WASTE_REPORT, {
    fetchPolicy: "no-cache",
  });
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport({
        variables: {
          startDate,
          endDate: endDate + " 23:59:59",
          branch_id: branchFilter ? parseInt(branchFilter) : undefined,
        },
      });
    }
    //eslint-disable-next-line
  }, [startDate, endDate, branchFilter]);

  const report: WasteReportType | null = data?.getWasteReport || null;

  return (
    <>
      <HeaderTitle
        title={lang("Merma y Desperdicio", "Waste & Spoilage")}
        btn={has_access(256) ? btn : null}
      />
      <InventoryNav active="waste" />

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-lg-3">
          <label className="small font-weight-bold">
            {lang("Desde", "From")}
          </label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-lg-3">
          <label className="small font-weight-bold">
            {lang("Hasta", "To")}
          </label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-lg-3">
          <label className="small font-weight-bold">
            {lang("Sucursal", "Branch")}
          </label>
          <select
            className="custom-select"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">
              {lang("Todas las sucursales", "All branches")}
            </option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {report && (
        <>
          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <div className="small text-muted">
                  {lang("Costo Total de Merma", "Total Waste Cost")}
                </div>
                <div className="h4 text-danger font-weight-bold mb-0">
                  {formatCurrency(report.total_waste_cost)}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <div className="small text-muted">
                  {lang("Total de Registros", "Total Entries")}
                </div>
                <div className="h4 font-weight-bold mb-0">
                  {report.total_entries}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <div className="small text-muted">
                  {lang("Ingrediente Más Desperdiciado", "Top Wasted Ingredient")}
                </div>
                <div className="h4 font-weight-bold mb-0">
                  {report.by_ingredient.length > 0
                    ? report.by_ingredient[0].ingredient?.name || "—"
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* By Reason */}
          <div className="row mb-4">
            <div className="col-lg-12">
              <div className="material_card p-3">
                <h6 className="font-weight-bold mb-3">
                  {lang("Por Razón", "By Reason")}
                </h6>
                {report.by_reason.length === 0 ? (
                  <p className="text-muted text-center py-3">
                    {lang(
                      "No hay registros de merma en este periodo",
                      "No waste records in this period"
                    )}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm vertical_middle">
                      <thead className="thead-light">
                        <tr>
                          <th>{lang("Razón", "Reason")}</th>
                          <th className="text-center">
                            {lang("Registros", "Entries")}
                          </th>
                          <th className="text-right">
                            {lang("Costo", "Cost")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.by_reason.map((r) => {
                          const info = WASTE_REASON_LABELS[r.reason] || {
                            label: r.reason,
                            labelEn: r.reason,
                            color: "secondary",
                          };
                          return (
                            <tr key={r.reason}>
                              <td>
                                <span className={`badge badge-${info.color}`}>
                                  {lang(info.label, info.labelEn)}
                                </span>
                              </td>
                              <td className="text-center">{r.count}</td>
                              <td className="text-right text-danger font-weight-bold">
                                {formatCurrency(r.total_cost)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* By Ingredient */}
          <div className="row">
            <div className="col-lg-12">
              <div className="material_card p-3">
                <h6 className="font-weight-bold mb-3">
                  {lang("Por Ingrediente", "By Ingredient")}
                </h6>
                {report.by_ingredient.length === 0 ? (
                  <p className="text-muted text-center py-3">
                    {lang(
                      "No hay registros de merma en este periodo",
                      "No waste records in this period"
                    )}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover vertical_middle">
                      <thead className="thead-light">
                        <tr>
                          <th>{lang("Ingrediente", "Ingredient")}</th>
                          <th className="text-center">
                            {lang("Cantidad", "Quantity")}
                          </th>
                          <th className="text-center">
                            {lang("Unidad", "Unit")}
                          </th>
                          <th className="text-center">
                            {lang("Registros", "Entries")}
                          </th>
                          <th className="text-right">
                            {lang("Costo", "Cost")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.by_ingredient.map((item) => (
                          <tr key={item.ingredient?.id || "unknown"}>
                            <td className="font-weight-bold">
                              {item.ingredient?.name || "—"}
                            </td>
                            <td className="text-center">
                              {Number(item.total_quantity).toFixed(2)}
                            </td>
                            <td className="text-center">
                              {item.ingredient?.unit || "—"}
                            </td>
                            <td className="text-center">{item.count}</td>
                            <td className="text-right text-danger font-weight-bold">
                              {formatCurrency(item.total_cost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WasteReportPage;
